import type React from 'react';
import {useState} from 'react';
import {Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {formatDate} from '~/pages/category-profile/domain/formatDate';
import {useMarketActions} from '../../hooks/useMarket';
import {LOCALE} from '../../domain/locale';
import type {PriceDriver, ShouldCost} from '../../types/shouldCostTypes';
import {ProfileSection} from '../components/ProfileSection';
import {RowAction} from '../components/RowAction';
import {DriverDialog, type DriverDraft} from '../market/DriverDialog';

type Props = {databagId: number; path: string; data: ShouldCost};

const draftOf = (d: PriceDriver): DriverDraft => ({
    name: d.series && d.name === d.series.name ? '' : d.name, seriesId: d.series?.id ?? null, unit: d.unit, weight: d.weight,
    prices: d.prices ?? [],
});

/**
 * Price drivers (before the price models): the category's cost factors, each following an index or priced by hand per
 * month. Price model lines move with their driver; the Market index tab shows and edits the same drivers.
 */
export const PriceDriversSection: React.FC<Props> = ({databagId, path, data}) => {
    const actions = useMarketActions(databagId, path);
    const [editing, setEditing] = useState<PriceDriver | 'new' | null>(null);
    const value = (d: PriceDriver) => d.latestValue === null ? '—'
        : `${d.latestValue.toLocaleString(LOCALE, {maximumFractionDigits: 4})}${d.unit ? ` ${d.unit}` : ''}`;

    return (
        <ProfileSection title="Price drivers" subtitle="What the price models' cost lines move with: an index, or prices you enter per month"
                        actions={data.canEdit && <Button size="small" startIcon={<Add/>} onClick={() => setEditing('new')}>Add a driver</Button>}>
            {data.drivers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No price drivers yet. Add one (for example Diesel, linked to an index, or Cleaning labour with its hourly rate
                    per month), then pick it on a price model&apos;s cost lines.
                </Typography>
            ) : (
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small" aria-label="Price drivers">
                        <TableHead>
                            <TableRow>
                                <TableCell>Driver</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell align="right">Latest</TableCell>
                                <TableCell align="right">Weight %</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.drivers.map(d => (
                                <TableRow key={d.id}>
                                    <TableCell><Typography variant="body2">{d.name}</Typography></TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {d.series ? d.series.name : `Entered by hand (${(d.prices ?? []).length} month${(d.prices ?? []).length === 1 ? '' : 's'})`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2">{value(d)}</Typography>
                                        {d.latestMonth && <Typography variant="caption" color="text.secondary">{formatDate(d.latestMonth)}</Typography>}
                                    </TableCell>
                                    <TableCell align="right">{Math.round(d.weight * 100)}</TableCell>
                                    <TableCell align="right">
                                        {data.canEdit && (
                                            <Stack direction="row" justifyContent="flex-end">
                                                <RowAction kind="edit" label={d.name} onClick={() => setEditing(d)}/>
                                                <RowAction kind="delete" label={d.name}
                                                           onClick={() => void actions.remove({mappingId: d.id, name: d.name})}/>
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
            {editing && (
                <DriverDialog driver={editing === 'new' ? undefined : draftOf(editing)} series={data.options.series}
                              onClose={() => setEditing(null)}
                              onSubmit={input => editing === 'new' ? actions.add(input) : actions.update(editing.id, input)}/>
            )}
        </ProfileSection>
    );
};
